/**
 * data/instrumentCatalog/metadata.ts
 * Comprehensive catalog version metadata for template sync detection
 * 
 * This metadata provides complete tracking of the instrument catalog state to enable
 * intelligent template synchronization. When users import instruments, they're linked
 * to this version. When we update the catalog, version comparison allows efficient
 * detection of which user instruments may need template updates.
 */

import type { CatalogMetadata } from '@/types/reference/InstrumentCatalog';

/**
 * Current instrument catalog metadata
 * 
 * ⚠️ IMPORTANT: Update this metadata whenever catalog content changes:
 * 
 * **Version Format**: YYYY.MM.PATCH (e.g., "2025.09.1")
 * - YYYY.MM: Year and month of release
 * - PATCH: Incremental fixes/updates within the month
 * 
 * **Update Process**:
 * 1. Modify catalog data (add/edit/remove instruments)
 * 2. Update version number (increment appropriately)  
 * 3. Update lastUpdated timestamp
 * 4. Update instrumentCount to match actual catalog
 * 5. Add meaningful release notes
 * 6. Regenerate checksum if integrity validation is needed
 */
export const CATALOG_METADATA: CatalogMetadata = {
  version: "2025.09.4",
  lastUpdated: "2025-01-09T20:00:00Z",
  
  // Catalog Statistics
  instrumentCount: 485,
  
  // Release Information
  releaseNotes: [
    "🔄 Template sync testing: Updated multiple instruments with symbol, name, and classification changes",
    "Enhanced template tracking system to detect all catalog field changes",
    "Added comprehensive field-level template synchronization (symbol, displayName, classification, contract specs)",
    "Testing complete catalog update detection workflow"
  ],
  
  // Data Integrity
  checksum: "sha256:a1b2c3d4e5f6789...", // TODO: Generate actual checksum when needed
  
  // Change Tracking
  previousVersion: "2025.09.3",
  changesSince: {
    added: 0,
    modified: 8, // Multiple instruments updated for template sync testing
    removed: 0
  },
  
  // Compatibility
  minRequiredVersion: "2025.09.0", // Oldest version that can safely sync with this
  breakingChanges: false
} as const;

/**
 * Helper function to generate version comparison key
 * Used for efficient version comparisons in template sync logic
 */
export const getVersionKey = (version: string): number => {
  const [yearStr, monthStr, patchStr = '0'] = version.split('.');
  const year = parseInt(yearStr || '0', 10);
  const month = parseInt(monthStr || '0', 10);
  const patch = parseInt(patchStr, 10);
  return (year * 10000) + (month * 100) + patch;
};

/**
 * Helper function to check if version A is newer than version B
 */
export const isVersionNewer = (versionA: string, versionB: string): boolean => {
  return getVersionKey(versionA) > getVersionKey(versionB);
};

/**
 * Helper function to format version for display
 */
export const formatVersionForDisplay = (version: string): string => {
  const [yearStr, monthStr, patchStr] = version.split('.');
  if (!yearStr || !monthStr) return version;
  
  const year = parseInt(yearStr, 10);
  const month = parseInt(monthStr, 10);
  const monthName = new Date(year, month - 1).toLocaleString('en-US', { month: 'long' });
  return patchStr && patchStr !== '0' ? `${monthName} ${year}.${patchStr}` : `${monthName} ${year}`;
};